import { fetchWithAuth } from '../utils/api';

export interface Round {
  id: number;
  format: string;
  status: string;
  round_code: string;
  created_at: string;
  started_at?: string;
  completed?: boolean;
  completed_time?: string;
  motion?: {
    id: number;
    text: string;
    theme?: {
      id: number;
      name: string;
    };
  };
  moderator?: {
    id: number;
    username: string;
    first_name?: string;
    last_name?: string;
  };
  created_by?: {
    id: number;
    username: string;
  };
}

// Define interfaces for the log structure received from the API
interface LogRound {
  id: number;
  format: string;
  round_code: string;
  motion?: {
    id: number;
    text: string;
    theme?: {
      id: number;
      name: string;
    };
  };
  moderator?: {
    id: number;
    username: string;
  };
}

interface User {
  id: number;
  username: string;
}

interface Log {
  id: number;
  round: LogRound;
  user?: User; // The user who was involved in the log
  // Other properties not needed for this function
}

interface LogDateGroup {
  date: string;
  logs: Log[];
}

export const fetchRounds = async (): Promise<Round[]> => {
  try {
    // First try the direct rounds endpoint
    try {
      const response = await fetchWithAuth('/api/admin/rounds/');
      if (Array.isArray(response) && response.length > 0) {
        return response.map((round: any) => {
          // Ensure we have both moderator and created_by info
          const moderatorInfo = round.moderator || (round.moderator_id ? {
            id: round.moderator_id,
            username: round.moderator_name || 'Moderator'
          } : undefined);
          
          const createdByInfo = round.created_by || (round.created_by_id ? {
            id: round.created_by_id,
            username: round.created_by_name || 'Creator'
          } : undefined);
          
          return {
            id: round.id,
            format: round.format,
            status: round.status || 'unknown',
            round_code: round.round_code || `R${String(round.id).padStart(5, '0')}`,
            created_at: round.created_at,
            started_at: round.started_at,
            completed: round.completed,
            completed_time: round.completed_time,
            motion: round.motion,
            moderator: moderatorInfo, 
            created_by: createdByInfo || moderatorInfo // Fall back to moderator if no created_by
          };
        });
      }
    } catch (error) {
      console.warn("Direct rounds endpoint failed, falling back to logs:", error);
    }
    
    // Use the logs attendance endpoint as fallback
    const response = await fetchWithAuth('/api/admin/logs/attendance/');
    
    // Extract unique rounds from the logs
    if (Array.isArray(response) && response.length > 0) {
      const uniqueRounds = new Map<number, Round>();
      
      // Process each date group
      response.forEach((dateGroup: LogDateGroup) => {
        if (dateGroup && Array.isArray(dateGroup.logs)) {
          // Process each log in the date group
          dateGroup.logs.forEach((log: Log) => {
            if (log && log.round && log.round.id) {
              // Only add if not already in map (or update if it has more information)
              const existingRound = uniqueRounds.get(log.round.id);
              
              if (!existingRound || !existingRound.motion) {
                // Extract creator info if available
                const creatorInfo = log.user ? {
                  id: log.user.id,
                  username: log.user.username
                } : undefined;
                
                uniqueRounds.set(log.round.id, {
                  id: log.round.id,
                  format: log.round.format,
                  status: 'unknown', // Status not available in logs
                  round_code: log.round.round_code || `R${String(log.round.id).padStart(5, '0')}`,
                  created_at: dateGroup.date, // Use the log date as an approximation
                  motion: log.round.motion,
                  moderator: log.round.moderator,
                  created_by: creatorInfo // Use the log user as creator if available
                });
              }
            }
          });
        }
      });
      
      // Convert Map values to array
      return Array.from(uniqueRounds.values());
    }
    
    return [];
  } catch (error) {
    console.error("Error fetching rounds:", error);
    return [];
  }
};

export const fetchRound = async (roundId: number): Promise<Round | null> => {
  try {
    const response = await fetchWithAuth(`/api/rounds/${roundId}/`);
    return response || null;
  } catch (error) {
    console.error(`Error fetching round ${roundId}:`, error);
    return null;
  }
}; 